'use client';

import {
  SearchBar,
} from '@/components/ui';
import * as Avatar from '@radix-ui/react-avatar';
import * as Dialog from '@radix-ui/react-dialog';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Edit,
  Eye,
  Info,
  Plus,
  RefreshCw,
  Trash2,
  User,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';

interface AuditLog {
  id: string;
  timestamp: string | Date; // API returns string (JSON serialized Date)
  userId: string;
  userName: string;
  userEmail: string;
  action: 'CREATE' | 'EDIT' | 'DELETE' | 'LOGIN' | 'LOGOUT' | 'EXPORT' | 'IMPORT' | 'VIEW' | 'DOWNLOAD' | 'UPLOAD';
  resourceType: 'BLOG' | 'FAQ' | 'USER' | 'MEDIA' | 'SERVICE' | 'SEO' | 'SYSTEM' | 'PAGE' | 'FILE';
  resourceId: string;
  resourceName: string;
  details: string;
  ipAddress: string;
  userAgent: string;
  status: 'SUCCESS' | 'FAILURE' | 'PENDING';
  metadata?: Record<string, unknown>;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface AuditLogsResponse {
  logs: AuditLog[];
  pagination: PaginationInfo;
}


export default function AuditLogs() {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [actionFilter, setActionFilter] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchAuditLogs = async (page = 1, search = '', action = '', resourceType = '', status = '', start = '', end = '') => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate date range before fetching
      if (start && end && new Date(start) > new Date(end)) {
        throw new Error('Start date must be before end date');
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        search,
        action,
        resourceType,
        status,
        startDate: start,
        endDate: end
      });

      const response = await fetch(`/api/admin/audit-logs?${params}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch audit logs' }));
        throw new Error(errorData.error || `Failed to fetch audit logs (${response.status})`);
      }

      const data: AuditLogsResponse = await response.json();

      // Validate response data
      if (!data || !Array.isArray(data.logs)) {
        throw new Error('Invalid response format from server');
      }

      setAuditLogs(data.logs);
      setPagination(data.pagination);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      setAuditLogs([]);
      console.error('Fetch audit logs error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs(1, searchQuery, actionFilter, resourceTypeFilter, statusFilter, startDate, endDate);
  }, [searchQuery, actionFilter, resourceTypeFilter, statusFilter, startDate, endDate]);

  const handlePageChange = (newPage: number) => {
    fetchAuditLogs(newPage, searchQuery, actionFilter, resourceTypeFilter, statusFilter, startDate, endDate);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setExportError(null);

      // Validate date range
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        setExportError('Start date must be before end date');
        return;
      }

      const params = new URLSearchParams({
        search: searchQuery,
        action: actionFilter,
        resourceType: resourceTypeFilter,
        status: statusFilter,
        startDate,
        endDate,
        format: 'csv'
      });

      const response = await fetch(`/api/admin/audit-logs/export?${params}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Export failed' }));
        throw new Error(errorData.error || `Export failed with status ${response.status}`);
      }

      const blob = await response.blob();

      // Check if blob is valid
      if (blob.size === 0) {
        throw new Error('No data to export');
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Show success message
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Export failed. Please try again.';
      setExportError(errorMessage);
      console.error('Export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE': return <Plus className="w-4 h-4 text-green-600" />;
      case 'EDIT': return <Edit className="w-4 h-4 text-blue-600" />;
      case 'DELETE': return <Trash2 className="w-4 h-4 text-red-600" />;
      case 'LOGIN': return <User className="w-4 h-4 text-purple-600" />;
      default: return <Info className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'FAILURE': return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'PENDING': return <Clock className="w-4 h-4 text-yellow-600" />;
      default: return <Info className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatTimestamp = (timestamp: string | Date) => {
    try {
      const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid Date';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-500">Track all user actions and system changes</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={isLoading || isExporting}
          >
            {isExporting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export CSV
              </>
            )}
          </button>
          {exportError && (
            <span className="text-sm text-red-600">{exportError}</span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="User, resource, or action..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none "
            >
              <option value="">All Actions</option>
              <option value="CREATE">Create</option>
              <option value="EDIT">Edit</option>
              <option value="DELETE">Delete</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
              <option value="EXPORT">Export</option>
              <option value="IMPORT">Import</option>
              <option value="VIEW">View</option>
              <option value="DOWNLOAD">Download</option>
              <option value="UPLOAD">Upload</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type</label>
            <select
              value={resourceTypeFilter}
              onChange={(e) => setResourceTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none "
            >
              <option value="">All Types</option>
              <option value="BLOG">Blog</option>
              <option value="FAQ">FAQ</option>
              <option value="USER">User</option>
              <option value="MEDIA">Media</option>
              <option value="SERVICE">Service</option>
              <option value="SEO">SEO</option>
              <option value="SYSTEM">System</option>
              <option value="PAGE">Page</option>
              <option value="FILE">File</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none "
            >
              <option value="">All Status</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILURE">Failure</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setExportError(null);
              }}
              max={endDate || undefined}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none "
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setExportError(null);
              }}
              min={startDate || undefined}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none "
            />
          </div>
        </div>
        {startDate && endDate && new Date(startDate) > new Date(endDate) && (
          <div className="text-sm text-amber-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            Start date must be before end date
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Showing <span className="font-medium">{auditLogs.length}</span> of <span className="font-medium">{pagination.total}</span> logs
            </span>
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Loading...
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-red-600">Error: {error}</span>
                <button
                  onClick={() => fetchAuditLogs(pagination.page, searchQuery, actionFilter, resourceTypeFilter, statusFilter, startDate, endDate)}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
          <div className="text-sm text-gray-500">
            {pagination.totalPages > 0 ? `Page ${pagination.page} of ${pagination.totalPages}` : 'No pages'}
          </div>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Resource
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Loading audit logs...
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-red-500">
                  {error}
                </td>
              </tr>
            ) : auditLogs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No audit logs found
                </td>
              </tr>
            ) : (
              auditLogs.map((log, index) => (
                <tr key={log.id || `log-${index}`} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {formatTimestamp(log.timestamp)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Avatar.Root className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Avatar.Fallback className="w-4 h-4 text-blue-600">
                          <User className="w-4 h-4 text-blue-600" />
                        </Avatar.Fallback>
                      </Avatar.Root>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{log.userName || 'Unknown'}</div>
                        <div className="text-sm text-gray-500">{log.userEmail || 'N/A'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getActionIcon(log.action)}
                      <span className="text-sm font-medium text-gray-900">{log.action || 'N/A'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{log.resourceType || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{log.resourceName || 'N/A'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate" title={log.details || ''}>
                      {log.details || 'No details'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log.status)}
                      <span className={`text-sm font-medium px-2 py-1 rounded-full ${log.status === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                        log.status === 'FAILURE' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                        {log.status || 'UNKNOWN'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                      aria-label="View log details"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrev || isLoading}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNext || isLoading}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="text-sm text-gray-500">
              {pagination.total} total logs
            </div>
          </div>
        </div>
      )}

      {/* Log Details Modal */}
      <Dialog.Root open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
          <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-lg font-medium text-gray-900">Audit Log Details</Dialog.Title>
                  <Dialog.Close asChild>
                    <button className="text-gray-400 hover:text-gray-600">
                      <X className="w-6 h-6" />
                    </button>
                  </Dialog.Close>
                </div>

                {selectedLog && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Timestamp:</span>
                        <div className="font-medium">{formatTimestamp(selectedLog.timestamp)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Action:</span>
                        <div className="font-medium">{selectedLog.action || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">User:</span>
                        <div className="font-medium">{selectedLog.userName || 'Unknown'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Email:</span>
                        <div className="font-medium">{selectedLog.userEmail || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <div className="font-medium">{selectedLog.status || 'UNKNOWN'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Resource Type:</span>
                        <div className="font-medium">{selectedLog.resourceType || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Resource Name:</span>
                        <div className="font-medium">{selectedLog.resourceName || 'N/A'}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">IP Address:</span>
                        <div className="font-medium">{selectedLog.ipAddress || 'N/A'}</div>
                      </div>
                    </div>

                    <div>
                      <span className="text-gray-500 text-sm">Details:</span>
                      <div className="mt-1 text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                        {selectedLog.details || 'No details available'}
                      </div>
                    </div>

                    {selectedLog.userAgent && (
                      <div>
                        <span className="text-gray-500 text-sm">User Agent:</span>
                        <div className="mt-1 text-sm text-gray-700 bg-gray-50 p-3 rounded-md break-all">
                          {selectedLog.userAgent}
                        </div>
                      </div>
                    )}

                    {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                      <div>
                        <span className="text-gray-500 text-sm">Metadata:</span>
                        <div className="mt-1 bg-gray-50 p-3 rounded-md overflow-x-auto">
                          <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                            {JSON.stringify(selectedLog.metadata, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end pt-4">
                      <Dialog.Close asChild>
                        <button className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">
                          Close
                        </button>
                      </Dialog.Close>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
